import { v3 } from "oc-threejs/short3";
import * as Commons from "oc-commons/Commons";
import * as Textures from "oc-threejs/Textures";
import * as Scenes from "oc-threejs/Scenes";
import { PosBox } from "oc-threejs/PosBox";
import TWEEN from "@tweenjs/tween.js";

import * as _ from "lodash";
import { MathUtils, Matrix4, Object3D, Vector3 } from "three";

declare const APP: any;
declare const STAGE: any;

declare module "three" {
  interface Material {
    id: number;
  }

  interface Object3D {
    _opacity?: number;
    opacity?: number;
    _tapable?: boolean;
    tapable?: boolean;
    _selected?: boolean;
    selected?: boolean;
    _draggable?: boolean;
    draggable?: boolean;
    _dragging?: boolean;
    dragging?: boolean;
    _hoverable?: boolean;
    hoverable?: boolean;
    _hovered?: any;
    hovering?: any;
    _receiveDropable?: boolean;
    receiveDropable?: boolean;
    _intersectionPriority?: number;
    intersectionPriority?: number;
    arithmeticNormal?: Vector3;
    posbox?: PosBox;
    posboxWorld?: PosBox;
    raycastVisibleTags?: any; // Define properly if possible, using any for now
    onBeforeRenderStage?: () => void;
    animateState_?: any;
    posbox_?: PosBox;

    onTap?(): void;
    acceptReceiveDrop?(object: any): boolean;
    onReceiveDrop?(object: any): void;
    onDrag?(): void;
    onDrop?(): void;
    onDragCancel?(): void;
    onDragBegin?(): void;
    onDragEnd?(): void;
    onDragEnter?(): void;
    onDragLeave?(real: boolean): void;
    onChangeInDragSelectHoverState?(): void;
    trigger(name: string, ...args: any[]): this;
    on(name: string, callback: (...args: any[]) => void): this;
    copyMatrix(matrix: Matrix4): void;
    rotateOnPivotAxis(pivotPoint: Vector3, axis: Vector3, angle: number): void;
    createPivotAxis(
      pivotPoint: Vector3,
      axis: Vector3,
      lastAngle?: number,
    ): (angle: number) => void;
    createPivotAxisWorld(
      pivotPointWorld: Vector3,
      axisWorld: Vector3,
      lastAngle?: number,
    ): (angle: number) => void;
    animate(params: any): void;
    traverseVisible(callback: (object: Object3D) => any): void;
  }
}

/**
 * Erweitert THREE um ein fehlendes Feature - eine vom Material unabhängige Opacity.
 * Wenn dieses Property ungleich undefined ist, dann wird die Opacity dieses Objectes gesetzt und
 * die jedes Child rekursiv damit multipliziert. Das Multiplizieren erfolgt in
 * Stage.onBeforeRenderStage, und es werden eventuell Kopien von Materialien angelegt wie mittels
 * Textures.highlight. Die Opacity der Material-Kopie wird mit this.opacity überschrieben.
 * https://github.com/mrdoob/three.js/issues/13440
 */
Commons.monkeyProperty(Object3D.prototype, "opacity", {
  get() {
    return (this as any)._opacity;
  },
  set(v: number) {
    (this as any)._opacity = v;
  },
});

/**
 * Ist das Objekt per Maus oder Touch anklickbar? Default ist false.
 */
Commons.monkeyProperty(Object3D.prototype, "tapable", {
  get() {
    return (this as any)._tapable === true;
  },
  set(v: boolean) {
    (this as any)._tapable = v && true;
  },
});

/**
 * Ist das Objekt gerade selektiert? Default ist false.
 * Selektierung muss man selbst implementieren, indem man
 * in onTap selected toggelt und dann onChangeInDragSelectHoverState
 * aufruf
 */
Commons.monkeyProperty(Object3D.prototype, "selected", {
  get() {
    return (this as any)._selected === true;
  },
  set(v: boolean) {
    (this as any)._selected = v && true;
  },
});

/**
 * Ist das Objekt per Maus oder Touch verschiebbar? Default ist false.
 */
Commons.monkeyProperty(Object3D.prototype, "draggable", {
  get() {
    return (this as any)._draggable === true;
  },
  set(v: boolean) {
    (this as any)._draggable = v && true;
  },
});

/**
 * Wird das Object gerade gedraggt?
 */
Commons.monkeyProperty(Object3D.prototype, "dragging", {
  get() {
    return (this as any)._dragging === true;
  },
  set(v: boolean) {
    (this as any)._dragging = v && true;
  },
});

/**
 * Hovered das Objekt, wenn die Maus drüberfährt? Default ist false.
 */
Commons.monkeyProperty(Object3D.prototype, "hoverable ", {
  get() {
    return (this as any)._hoverable === true;
  },
  set(v: boolean) {
    (this as any)._hoverable = v && true;
  },
});

/**
 * Wird das Object gerade gehovered? Kann false, true oder ein Objekt sein, das per receiveDrop
 * gedraggt und potentiell empfangen wird. Null gilt als hovering, undefined als nicht hovering.
 */
Commons.monkeyProperty(Object3D.prototype, "hovering", {
  get() {
    return (this as any)._hovered == true;
  },
  set(v: any) {
    (this as any)._hovered = v;
  },
});

/**
 * Können dem Objekt per Drag&Drop Datenobjekte wie Materialien oder Typ zugewiesen werden? Default
 * ist false.
 */
Commons.monkeyProperty(Object3D.prototype, "receiveDropable", {
  get() {
    return (this as any)._receiveDropable === true;
  },
  set(v: boolean) {
    (this as any)._receiveDropable = v && true;
  },
});

/**
 * Mit der intersectionPriority wird für Screens.intersectionAtMouse festgelegt, dass ein Objekt
 * eine höhere Priorität hat, also auch dann bei einem tap / drag / hover gewertet wird, wenn es
 * durch ein Objekt niedrigerer Priorität verdeckt wird.
 */
Commons.monkeyProperty(Object3D.prototype, "intersectionPriority", {
  get() {
    return (this as any)._intersectionPriority || 0;
  },
  set(v: number) {
    (this as any)._intersectionPriority = v;
  },
});

/**
 * von Martin für den Druckdesigner
 * Ein NormalenVektor, der sich aus dem arithmetischen mittel aller Normalen des Objektes berechnet
 * Funktioniert bei einer Kugel eher schlecht, es kommt ein Nullvektor raus, bei asymmetrischen
 * Objekten klappt das aber Recht gut. Ich hab das beim Druckdesigner benutzt, um mit der Kamera
 * auf ein Objekt zu zoomen
 */
Commons.monkeyProperty(Object3D.prototype, "arithmeticNormal", {
  get() {
    const direction = v3(0, 0, 0);
    const normals = (this as any).geometry.attributes.normal.array;
    let modelIndex = 0;

    let parent: any = (this as any).parent;
    while (parent) {
      APP.metadata.Model.forEach((model: any, index: number) => {
        if (model.Path.contains(parent.name)) {
          modelIndex = index;
          parent = false;
        }
      });
      if (parent) parent = parent.parent;
    }

    for (let i = 0; i < normals.length; i += 3) {
      direction.x += normals[i];
      direction.y += normals[i + 1];
      direction.z += normals[i + 2];
    }
    direction.x /= normals.length / 3;
    direction.y /= normals.length / 3;
    direction.z /= normals.length / 3;
    direction.applyQuaternion(APP.metadata.Model[modelIndex].Rotation);

    return direction;
  },
});

/**
 * Erweitert THREE.Object3D.prototype um einen Satz von Methoden für DragDrop, Tap, Hover und
 * ReceiveDrop (Zuweisen von Eigenschaften wie Materialien per Drag&Drop)
 */
Commons.monkeyPatch(Object3D.prototype, {
  /**
   * Wird bei Tap/Click auf das Objekt aufgerufen, wenn es tapable==true ist.
   * Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   */
  onTap() {},

  /**
   * Wird beim Droppen eines 2D-Objektes wie eine Textur auf das 3D-Objekt aufgerufen, wenn es
   * acceptReceivable==true ist, und soll zurückgegeben, ob das gedraggte Object entgegengenommen
   * werden könnte. Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   * @param object das gedroppte Datenobjekt
   * @returns boolean
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  acceptReceiveDrop(object: any) {
    return true;
  },

  /**
   * Wird beim Droppen eines 2D-Objektes wie einer Textur auf das 3D-Objekt aufgerufen,
   * wenn es acceptReceiveDrop && acceptReceiveDrop(object) ist, und sollte die eigentliche
   * Aktion auslösen. Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   * @param object das gedroppte Datenobjekt
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onReceiveDrop(object: any) {},

  /**
   * Wird bei jeder Veränderung der 3D-Position während des draggings aufgerufen.
   * Das Objekt hat dann bereits die neue Position erhalten.
   * Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   */
  onDrag() {},

  /**
   * Wird beim Drop aufgerufen. Das Objekt hat dann bereits die neue Position erhalten.
   * Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   */
  onDrop() {},

  /**
   * Wird einmalig aufgerufen, wenn der Dragvorgang abgebrochen wurde (also z.B. ausserhalb des
   * 3D-Bereichs fallen gelassen wurde) Callback-Methode, die in abgeleiteten Klassen
   * ausimplementiert werden kann.
   */
  onDragCancel() {
    // Standardmäßig wird das Objekt gelöscht, wenn es aus der Szene rausgedropt wird
    Scenes.remove(this);
  },

  /**
   * Wird zu Beginn eines Drag-Vorganges einmalig aufgerufen.
   * Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   */
  onDragBegin() {},

  /**
   * Wird zu Ende eines Drag-Vorganges einmalig aufgerufen, auch dann, wenn der Dragvorgang nicht
   * erfolgreich war. Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   */
  onDragEnd() {},

  /**
   * DragEnter wird als Teil des normalen Lifecycles bei jedem
   * Drag-Vorgang geworfen. Über real=true kann erkannt werden, ob
   * die Maus wirklich von ausserhalb der Szene kam.
   * Zum Überschreiben in abgeleiteteten Klassen.
   * Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   */
  onDragEnter() {},

  /**
   * Wird beim Verlassen der Scene aufgerufen, siehe DragEnter.
   * Callback-Methode, die in abgeleiteten Klassen ausimplementiert werden kann.
   * @param real if the mouse goes to the outside
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDragLeave(real: boolean) {},

  /**
   * Wird bei Ändern des Drag und Hover-States des Objektes aufgerufen. Die Methode sollte auf
   * dragging oder hovering prüfen und visuell reagieren. Callback-Methode, die in abgeleiteten
   * Klassen ausimplementiert werden kann.
   */
  onChangeInDragSelectHoverState() {
    if (this.dragging) {
      Textures.highlight(this, {
        opacity: STAGE.settings.dragDrop.dragOpacity,
        multiplyOpacity: true,
      });
    } else if (this.hovering) {
      Textures.highlight(this, {
        color: 0x93b4ed,
      });
    } else if (this.selected) {
      Textures.highlight(this, {
        color: 0xeae693,
      });
    } else {
      Textures.unhighlight(this);
    }
    STAGE.dirty();
  },

  /**
   * ruft alle Listener zu einem Namen auf mit optionalen Parametern
   */
  trigger(name: string, ...args: any[]) {
    this.dispatchEvent({
      type: name,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      args,
    });
    return this;
  },

  /**
   * registriert einen Listener, der immer aufgerufen wird
   * @param name der Name des Events
   * @param callback der zu registrierende Callback / Listener
   */
  on(name: string, callback: (...args: any[]) => void) {
    this.addEventListener(name, (event: any) => callback(...event.args));
    return this;
  },
});

/**
 * Erweitert THREE.Object3D.prototype um einen Satz von allgemeinen Methoden
 */
Commons.monkeyPatch(Object3D.prototype, {
  /**
   * Oft möchte man für Objekte bestimmen, ob sie an bestimmten Aktionen wie drag, hover oder tap
   * teilnehmen. In der Regel werden dabei Raycasts in die Szene geschickt und geschaut, welche
   * Objekte treffen. Der Raycaster betrachtet nur Objekte, die visible sind. Mit dem Feld
   * raycastVisibleTags kann an Objekten festgelegt werden, ob sie für eine bestimmte
   * Raycast-Aktionen visible sind oder nicht. Siehe changeRaycastVisibilityDuring.
   */
  raycastVisibleTags: undefined,

  /**
   * onBeforeRenderStage() wird von der Stage auf allen Scenen-Objekten aufgerufen, unabhängig
   * davon, ob sie visible sind, und vor onBeforeRender. Das ist sinnvoll, weil onBeforeRender
   * von Three nur für Objekte aufgrufen wird, die wirklich im Sichtfeld der Kamera liegen, und
   * weil es evenutell zu spät erfolgt - möchte man z.B. die Position von Objekten ändern, ist
   * das in onBeforeRenderScene zu spät.
   *
   * Das hier dient nur als Dummy, um anzugzeigen, dass es die Methode gibt. Sie wird nur
   * aufgerufen, wenn sie nicht undefined ist.
   */
  onBeforeRenderStage: undefined,

  /**
   * Übernimmt die Matrix komplett (Object3D.applyMatrix multipliziert dagegen).
   * @param {THREE.Matrix4} matrix
   */
  copyMatrix(matrix: Matrix4) {
    this.matrix.copy(matrix);
    this.matrix.decompose(this.position, this.quaternion, this.scale);
  },

  /**
   * Rotiert das Objekt um die Drehachse, die durch pivotPoint (=Drehpunkt) in Richtung axis
   * geht.
   * Im Gegensatz dazu ist bei rotateOnAxis der Drehpunkt nicht definierbar (ist da immer die
   * Position des Objektes). Wenn man ein Objekt mehrmals dreht (z.B. als Teil einer Animation),
   * sollte man aus Effizienzgründen createPivotAxis benutzen und die zurückgegebene Funktion
   * wiederverwenden.
   * @param {THREE.Vector3} pivotPoint Punkt in lokalem Objektspace
   * @param {THREE.Vector3} axis Achse in lokalem Objektspace
   * @param {number} angle
   */
  rotateOnPivotAxis(pivotPoint: Vector3, axis: Vector3, angle: number) {
    this.createPivotAxis(pivotPoint, axis)(angle);
  },

  /**
   * Gibt eine Drechachsen-Funktion zurück, die als Parameter einen Winkel entgegennimmt, um
   * welcher das Objekt gedreht wird. Die Drehung gilt bei mehrfachem Aufruf absolut zur
   * Ursprungsposition, nicht als jeweiliges Delta. Die Drehachse geht durch pivotPoint
   * (=Drehpunkt) in Richtung axis.
   * @param {THREE.Vector3} pivotPoint Punkt in lokalem Objektspace
   * @param {THREE.Vector3} axis Achse in lokalem Objektspace
   * @return Drehcallback, welches einen Winkel in Bezug zur ursprünglichen Orientierung erwartet
   */
  createPivotAxis(pivotPoint: Vector3, axis: Vector3, lastAngle?: number) {
    const translate0 = new Matrix4().makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);
    const translate1 = new Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);
    const rotation = new Matrix4();
    return (angle: number) => {
      this.matrix
        .multiply(translate0)
        .multiply(rotation.makeRotationAxis(axis, angle - (lastAngle || 0)))
        .multiply(translate1)
        .decompose(this.position, this.quaternion, this.scale);
      lastAngle = angle;
    };
  },

  /**
   * Wie createPivotAxis, aber im World-Koordinatensystem statt im lokalem Objektspace des Parents
   * @param {THREE.Vector3} pivotPointWorld
   * @param {THREE.Vector3} axisWorld
   */
  createPivotAxisWorld(pivotPointWorld: Vector3, axisWorld: Vector3, lastAngle?: number) {
    const pivotPoint = this.worldToLocal(pivotPointWorld.clone());
    const axis = this.worldToLocal(axisWorld).sub(this.worldToLocal(new Vector3(0, 0, 0)));
    return this.createPivotAxis(pivotPoint, axis, lastAngle);
  },

  /**
   * Definiert eine einfache Animation auf dem Objekt, entweder opacity, rotation oder
   * tranlation.
   * Es wird ein Parameterobjekt mit folgenden Parametern erwartet:
   *
   *   // Die Animation kann und/oder auf rotation, translation oder opacity wirken.
   *   // Wenn ein entsprechender Parameter gesetzt ist, dann wirkt er entsprechend.
   *   // Wenn man komplexere Animationen braucht, muss man die mit TWEEN selbst implementieren,
   *   // was nicht wirklich schwer ist
   *
   *   // rotate = Ziel-Drehwinkel in rad. Die Ausgangsstellung, um eine Rotation zurückzunehmen,
   * ist immer 0. rotate: null,
   *   // origin = Drehpunkt im Welt-Koordinatensystem,
   *   origin: v3(0,0,0),
   *   // axis = Drehachse im Welt-Koordinatensystem
   *   axis: v3(0,1,0),
   *   // die Zielposition als Ortsvektor, Angabe im lokalen Koordinatensystem des Parents
   *   targetPosition: null,
   *   // Ziel-Undurchsichtigkeit
   *   opacity: null,
   *   // Dauer der Animation in Millisekunden
   *   duration: 1000,
   *   // Timing-Funktion
   *   timing: TWEEN.Easing.Sinusoidal.InOut,
   *   // Läuft die Animation am Ende vom Ende zurück zum Anfang?
   *   yoyo: false,
   *   // Anzahl der Durchläufe. Für unendlich: Number.POSITIVE_INFINITY
   *   repeat: 1,
   *   // Zeitverzögerung bevor die Animation startet in Millisekunden
   *   delay: 0,
   *   // Callback, das direkt vor dem ersten Animationsschritt aufgerufen wird
   *   onStart: null,
   *   // Callback, das direkt nach dem letzten Animationsschritt aufgerufen wird
   *   onComplete: null,
   *
   * @param params
   */
  animate(params: any) {
    params = Commons.defaultsDeepSafe({}, params, {
      // Die Animation kann und/oder auf rotation, translation oder opacity wirken.
      // Wenn ein entsprechender Parameter gesetzt ist, dann wirkt er entsprechend.
      // Wenn man komplexere Animationen braucht, muss man die mit TWEEN selbst implementieren,
      // was nicht wirklich schwer ist
      // rotate = Ziel-Drehwinkel in rad. Die Ausgangsstellung, um eine Rotation zurückzunehmen, ist immer 0.
      rotate: null,
      // origin = Drehpunkt im Welt-Koordinatensystem,
      origin: new Vector3(0, 0, 0),
      // axis = Drehachse im Welt-Koordinatensystem
      axis: new Vector3(0, 1, 0),
      // die Zielposition als Ortsvektor, Angabe im lokalen Koordinatensystem des Parents
      position: null,
      // Ziel-Undurchsichtigkeit
      opacity: null,
      // Dauer der Animation in Millisekunden
      duration: 1000,
      // Timing-Funktion
      timing: TWEEN.Easing.Sinusoidal.InOut,
      // Läuft die Animation am Ende vom Ende zurück zum Anfang?
      yoyo: false,
      // Anzahl der Wiederholungen (0 = nur ein mal). Für unendlich: Number.POSITIVE_INFINITY
      repeat: 0,
      // Zeitverzögerung bevor die Animation startet in Millisekunden
      delay: 0,
      // Callback, das direkt vor dem ersten Animationsschritt aufgerufen wird
      onStart: null,
      // Callback, das direkt nach dem letzten Animationsschritt aufgerufen wird
      onComplete: null,
    });
    if (typeof this.animateState_ === "undefined") {
      this.animateState_ = {
        rotate: 0,
        lastOrigin: null,
        lastAxis: null,
        lastRotateFunction: null,
        opacity: ((v: any) => (v == null ? 1 : v))(_.get(this, "material.opacity")),
        tweenInstance: null,
      };
    } else if (this.animateState_.tweenInstance) {
      this.animateState_.tweenInstance.stop();
      this.animateState_.tweenInstance = null;
    }
    let rotationFunction: any = null;
    if (params.rotate != null) {
      if (
        this.animateState_.lastRotateFunction &&
        params.origin.equals(this.animateState_.lastOrigin) &&
        params.axis.equals(this.animateState_.lastAxis)
      ) {
        rotationFunction = this.animateState_.lastRotateFunction;
      } else {
        rotationFunction = this.createPivotAxisWorld(
          params.origin,
          params.axis,
          this.animateState_.rotate,
        );
        this.animateState_.lastRotateFunction = rotationFunction;
        this.animateState_.lastOrigin = params.origin.clone();
        this.animateState_.lastAxis = params.axis.clone();
      }
    }

    const interpolations: ((t: number) => void)[] = [];

    if (params.opacity != null) {
      const from = this.animateState_.opacity;
      const to = params.opacity;
      interpolations.push((t) => {
        const o = MathUtils.lerp(from, to, t);
        this.opacity = o;
        this.animateState_.opacity = o;
      });
    }
    if (params.rotate != null) {
      const from = this.animateState_.rotate;
      const to = params.rotate;
      interpolations.push((t) => {
        const o = MathUtils.lerp(from, to, t);
        rotationFunction(o);
        this.animateState_.rotate = o;
      });
    }
    if (params.position != null) {
      const from = this.position.clone();
      const to = params.position.clone();
      interpolations.push((t) => {
        this.position.lerpVectors(from, to, t);
      });
    }

    const time = {
      t: 0,
    };

    const update = () => {
      interpolations.forEach((callback) => callback(time.t));
      STAGE.dirty();
    };

    if (params.duration == 0) {
      time.t = 1;
      update();
      return;
    }

    const tween = (this.animateState_.tweenInstance = new TWEEN.Tween(time)
      .to({ t: 1 }, params.duration)
      .easing(params.timing)
      .onUpdate(update)
      .yoyo(params.yoyo)
      .repeat(params.repeat));

    if (params.onStart) {
      tween.onStart(params.onStart);
    }

    tween.onComplete(() => {
      time.t = 1;
      update();
      if (params.onComplete) {
        params.onComplete();
      }
    });

    if (params.delay > 0) {
      tween.delay(params.delay);
    }

    tween.start(STAGE.tweenStartNowTime);
    // wichtig ist dieses Dirty, um die Animation in Gang zu bringen
    STAGE.dirty();
  },
});

/**
 * Warnt vor Fehlern, die durch die Veränderung von Objekthierarchien während der Traversierung
 * entstehen
 */
let ___traverseCounter = 0;

const Object3D_orig_traverse = Object3D.prototype.traverse;
Object3D.prototype.traverse = function (callback: (object: Object3D) => any) {
  ___traverseCounter++;
  try {
    Object3D_orig_traverse.apply(this, [callback]);
  } finally {
    ___traverseCounter--;
  }
};

const Object3D_orig_traverseVisible = Object3D.prototype.traverseVisible;
Object3D.prototype.traverseVisible = function (callback: (object: Object3D) => any) {
  ___traverseCounter++;
  try {
    Object3D_orig_traverseVisible.apply(this, [callback]);
  } finally {
    ___traverseCounter--;
  }
};

const Object3D_orig_remove = Object3D.prototype.remove;
Object3D.prototype.remove = function (...object: Object3D[]) {
  if (___traverseCounter > 0) {
    console.warn(
      this +
        ".remove(" +
        object +
        ") called during traverse is error prone! Better use Scenes.filter(object).forEach(...)",
    );
  }
  return Object3D_orig_remove.apply(this, object);
};

/**
 * Gibt eine lazy erzeugte PosBox im World-Koordinatensystem zu diesem Virtual3D zurück
 * @return {PosBox}
 */
Commons.monkeyProperty(Object3D.prototype, "posbox", {
  get() {
    if ((this as any).posbox_ === undefined) {
      (this as any).posbox_ = new PosBox(this);
    }
    return (this as any).posbox_;
  },
  /**
   * @param {PosBox} value
   */
  set(value: PosBox) {
    (this as any).posbox_ = value;
  },
});

/**
 * Gibt eine lazy erzeugte PosBox im World-Koordinatensystem zu diesem Virtual3D zurück
 * @return {PosBox}
 */
Commons.monkeyProperty(Object3D.prototype, "posboxWorld", {
  get() {
    if ((this as any).posbox_ === undefined) {
      (this as any).posbox_ = new PosBox(this);
    }
    return (this as any).posbox_;
  },
});
